import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

import { toast } from 'react-toastify';

import NativeTable from '../../components/NativeTable/NativeTable';
import Loader from '../../components/Loader';
import Widget from '../../components/Widget';

import { getWorkperiodDetailRequest, getWorkperiodUsersRequest, clearWorkperiodDetail, endWorkperiodRequest, downloadWorkperiodCloseoutRequest, downloadWorkperiodPooledTipReportRequest, sendWorkperiodPooledTipReportRequest, payWorkperiodPooledTipsRequest, getWorkperiodPooledTipsAvailabilityRequest } from '../../actions/sales';
import {
    formatApiDateTime,
    formatApiTime,
    formatApiDateLong,
    parseApiDateTime,
    apiDateToYmd,
} from '../../utils/merchantLocalDateTime';
import { withTranslation } from '../../i18n/LanguageContext';

const cardStyle = {
    background: 'var(--card_bg)',
    borderRadius: '8px',
    padding: '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    minWidth: '140px',
    flex: '1 1 160px',
};

const cardLabelStyle = {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-light2)',
    marginBottom: '4px',
};

const cardValueStyle = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--white)',
};

const sectionTitleStyle = {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: 'var(--text-light2)',
    marginBottom: '8px',
    marginTop: '24px',
};

const closeoutButtonStyle = {
    ...cardStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    border: '1px solid rgba(126, 87, 194, 0.45)',
    background: 'linear-gradient(135deg, rgba(126, 87, 194, 0.18), rgba(91, 60, 147, 0.28))',
    color: 'var(--white)',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '84px',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
};

const POOLED_ACCENT = '#26c6da';

function downloadButtonStyle({ compact, downloading }) {
    return {
        ...closeoutButtonStyle,
        flex: '1 1 auto',
        minHeight: compact ? 0 : '84px',
        padding: compact ? '4px 10px' : '16px 20px',
        fontSize: compact ? '0.78rem' : '0.95rem',
        gap: compact ? '6px' : '10px',
        opacity: downloading ? 0.7 : 1,
        cursor: downloading ? 'not-allowed' : 'pointer',
        boxShadow: downloading ? 'none' : '0 4px 12px rgba(126, 87, 194, 0.2)',
    };
}

function pooledOutlineButtonStyle({ downloading, disabled }) {
    return {
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flex: '1 1 auto',
        minHeight: 0,
        padding: '4px 10px',
        border: `1px solid ${POOLED_ACCENT}`,
        background: 'transparent',
        color: POOLED_ACCENT,
        fontSize: '0.75rem',
        fontWeight: 700,
        cursor: downloading || disabled ? 'not-allowed' : 'pointer',
        opacity: downloading ? 0.7 : (disabled ? 0.5 : 1),
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        transition: 'transform 0.15s ease, opacity 0.15s ease, background 0.15s ease',
    };
}

function pooledFillButtonStyle({ loading, disabled }) {
    return {
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flex: '1 1 auto',
        minHeight: 0,
        padding: '4px 10px',
        border: `1px solid ${POOLED_ACCENT}`,
        background: 'rgba(38, 198, 218, 0.18)',
        color: POOLED_ACCENT,
        fontSize: '0.75rem',
        fontWeight: 700,
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : (disabled ? 0.5 : 1),
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        transition: 'transform 0.15s ease, opacity 0.15s ease, background 0.15s ease',
    };
}

function SummaryCard({ label, value, color, note }) {
    return (
        <div style={cardStyle}>
            <div style={cardLabelStyle}>{label}</div>
            <div style={{ ...cardValueStyle, color: color || 'var(--white)' }}>{value}</div>
            {note ? (
                <div
                    style={{
                        marginTop: '6px',
                        fontSize: '0.7rem',
                        color: 'var(--text-light2)',
                        lineHeight: 1.3,
                    }}
                >
                    {note}
                </div>
            ) : null}
        </div>
    );
}

class WorkperiodDetail extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        detail: PropTypes.object,
        detailLoading: PropTypes.bool,
        users: PropTypes.array,
        usersLoading: PropTypes.bool,
        toggling: PropTypes.bool,
        history: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
    };

    static defaultProps = {
        detail: null,
        detailLoading: false,
        users: [],
        usersLoading: false,
        toggling: false,
    };

    state = {
        // Period-scoped: true only when THIS work period ran under Pooled Tips
        // (backend pooledTipsEnabled from the availability endpoint), never the
        // account-level toggle alone.
        periodPooled: false,
        sendPoolLoading: null,
        pooledDownloadLoading: false,
        payPooledLoading: false,
        pooledPayAvailable: false,
    };

    handleEndPeriod = () => {
        const periodId = this.props.match.params.periodId;
        if (window.confirm('Are you sure you want to end this work period? This will clock out all active users.')) {
            this.props.dispatch(endWorkperiodRequest(periodId));
        }
    }

    handleDownloadCloseout = () => {
        const periodId = this.props.match.params.periodId;
        this.props.dispatch(downloadWorkperiodCloseoutRequest(periodId));
    }

    handleDownloadPooledTipReport = async () => {
        const periodId = this.props.match.params.periodId;
        this.setState({ pooledDownloadLoading: true });
        try {
            await this.props.dispatch(downloadWorkperiodPooledTipReportRequest(periodId));
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ pooledDownloadLoading: false });
        }
    }

    handleSendPooledTipReport = async (channel) => {
        const periodId = this.props.match.params.periodId;
        const workers = this.props.users || [];
        if (!workers.length) {
            toast.error('No employees with sales in this work period.');
            return;
        }

        const label = channel === 'sms' ? 'text' : 'email';
        const count = workers.length;
        if (!window.confirm(`Send the pooled tip report by ${label} to ${count} worker${count === 1 ? '' : 's'}?`)) {
            return;
        }

        this.setState({ sendPoolLoading: channel });
        try {
            const result = await this.props.dispatch(sendWorkperiodPooledTipReportRequest(periodId, channel));
            const parts = [`Sent ${result.sent}`];
            if (result.skipped) {
                parts.push(`skipped ${result.skipped} (no ${channel === 'sms' ? 'phone' : 'email'})`);
            }
            if (result.failed) {
                parts.push(`${result.failed} failed`);
            }
            toast.success(parts.join('; '));
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ sendPoolLoading: null });
        }
    }

    handlePayPooledTips = async () => {
        const periodId = this.props.match.params.periodId;
        if (this.isStillActive(this.props.detail)) {
            toast.error('End the work period before paying pooled tips.');
            return;
        }
        if (!window.confirm(
            'Pay pooled tip shares via Stripe for this work period?\n\n'
            + 'Each eligible employee receives their equal clock-session share. '
            + 'Employees already paid are skipped. Requires Tip Routing = Platform application fee.\n\n'
            + 'Each unpaid share is paid in full or held — no partial payments.'
        )) {
            return;
        }

        this.setState({ payPooledLoading: true });
        try {
            const result = await this.props.dispatch(payWorkperiodPooledTipsRequest(periodId));
            const parts = [];
            if (result.completed) parts.push(`${result.completed} paid`);
            if (result.skipped) parts.push(`${result.skipped} skipped`);
            if (result.held) parts.push(`${result.held} held until the full share can be paid`);
            if (result.failed) parts.push(`${result.failed} failed`);
            if (!parts.length && result.message) {
                toast.info(result.message);
            } else if (result.failed) {
                toast.warning(parts.join('; ') || result.message || 'Pooled tip payout finished with failures');
            } else if (result.held) {
                toast.warning(parts.join('; ') || result.message || 'Some shares are held until the full amount can be paid');
            } else {
                toast.success(parts.join('; ') || result.message || 'Pooled tips settled');
            }
            this.props.dispatch(getWorkperiodUsersRequest(periodId));
        } catch (err) {
            // toast shown in action
        } finally {
            this.setState({ payPooledLoading: false });
            this.loadPooledPayAvailability();
        }
    }

    loadPooledPayAvailability = async () => {
        const periodId = this.props.match.params.periodId;
        if (!periodId) return;
        try {
            const availability = await this.props.dispatch(
                getWorkperiodPooledTipsAvailabilityRequest(periodId)
            );
            if (this._isMounted !== false) {
                this.setState({
                    periodPooled: !!availability?.pooledTipsEnabled,
                    pooledPayAvailable: !!availability?.showPayButton,
                });
            }
        } catch (err) {
            this.setState({ periodPooled: false, pooledPayAvailable: false });
        }
    }

    componentDidMount() {
        const periodId = this.props.match.params.periodId;
        this.props.dispatch(getWorkperiodDetailRequest(periodId));
        this.props.dispatch(getWorkperiodUsersRequest(periodId));
        // Pooled display is period-scoped -- the availability endpoint reports
        // whether THIS period ran pooled, regardless of the account-level toggle.
        this.loadPooledPayAvailability();
    }

    componentWillUnmount() {
        this.props.dispatch(clearWorkperiodDetail());
    }

    componentDidUpdate(prevProps) {
        const wasOpen = prevProps.detail && this.isStillActive(prevProps.detail);
        const nowEnded = this.props.detail && !this.isStillActive(this.props.detail);
        if (wasOpen && nowEnded) {
            this.loadPooledPayAvailability();
        }
    }

    formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '$0.00';
        const n = Number(value);
        if (Number.isNaN(n)) return '$0.00';
        return `$${n.toFixed(2)}`;
    }

    // Detail Start/End/ClockIn/ClockOut are already merchant-local from the API.
    formatDateTime = (raw) => formatApiDateTime(raw, '—');

    formatTime = (raw) => formatApiTime(raw, '—');

    formatHours = (value) => {
        if (value === null || value === undefined) return '—';
        const n = Number(value);
        if (Number.isNaN(n)) return '—';
        const hours = Math.floor(n);
        const minutes = Math.round((n - hours) * 60);
        return `${hours}h ${minutes}m`;
    }

    isStillActive = (detail) => {
        const end = this.v(detail, 'end', 'End', 'endUtc', 'EndUtc');
        return !end || String(end).trim() === '' || String(end).trim() === '0001-01-01T00:00:00';
    }

    getElapsedHours = (detail) => {
        const startRaw = this.v(detail, 'start', 'Start', 'startUtc', 'StartUtc');
        const startDate = parseApiDateTime(startRaw);
        if (!startDate) return '—';
        const now = new Date();
        const diffMs = now - startDate;
        if (diffMs < 0) return '0h 0m';
        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    }

    v = (obj, ...keys) => {
        for (const k of keys) {
            if (obj[k] !== null && obj[k] !== undefined) return obj[k];
        }
        return 0;
    }

    render() {
        const { detail, detailLoading, users, usersLoading } = this.props;

        if (detailLoading) {
            return (
                <div className="page-wrapper">
                    <div className="page-content">
                        <Loader size={80} />
                    </div>
                </div>
            );
        }

        if (!detail) {
            return (
                <div className="page-wrapper">
                    <div className="page-content">
                        <p>No detail data available.</p>
                        <button className="btn btn-sm" style={{ background: '#7e57c2', color: '#fff' }} onClick={() => this.props.history.push('/admin/sales')}>
                            <i className="zmdi zmdi-arrow-left" aria-hidden="true"></i> Back to Work Periods
                        </button>
                    </div>
                </div>
            );
        }

        const card = detail.card || detail.Card || {};
        const cash = detail.cash || detail.Cash || {};
        const membership = detail.membership || detail.Membership || {};
        // *NET = all-tender merch (same meaning as list Charges). Not list CARD NET.
        const cashAmt = Number(this.v(cash, 'amount', 'Amount') || 0);
        const cardAmt = Number(this.v(card, 'amount', 'Amount') || 0);
        const memberAmt = Number(this.v(membership, 'amount', 'Amount') || 0);
        const adminFeesAmt = Number(this.v(detail, 'adminFees', 'AdminFees') || 0);
        // Customer / Stripe / Rebelity fees stay in API + generated reports; not shown on this UI.
        // Detail Net = all-tender merch (*NET). Do not use list-style v() (missing → 0).
        const netSalesRaw = detail.net !== undefined && detail.net !== null
            ? detail.net
            : (detail.Net !== undefined && detail.Net !== null ? detail.Net : null);
        const netSalesParsed = netSalesRaw === null || netSalesRaw === '' ? NaN : Number(netSalesRaw);
        const tenderMerch = cashAmt + cardAmt + memberAmt;
        const netSales = Number.isFinite(netSalesParsed) ? netSalesParsed : tenderMerch;
        const linkedEvent = detail.event || detail.Event;
        const eventName = linkedEvent && (linkedEvent.eventName || linkedEvent.EventName);
        const instanceName = linkedEvent && (linkedEvent.instanceName || linkedEvent.InstanceName);
        const instanceDate = linkedEvent && (linkedEvent.instanceDate || linkedEvent.InstanceDate);
        const organizer = linkedEvent && (linkedEvent.organizer || linkedEvent.Organizer);
        const eventTitle = eventName && instanceName
            ? `${eventName}: ${instanceName}`
            : eventName;
        const manualName = !eventName && String(detail.name || detail.Name || '').trim();
        const bannerTitle = eventTitle || manualName;
        const bannerLabel = eventName ? 'Event' : 'Name';
        const ticketsSoldRaw = linkedEvent && (linkedEvent.ticketsSold ?? linkedEvent.TicketsSold);
        const checkedInRaw = linkedEvent && (linkedEvent.checkedIn ?? linkedEvent.CheckedIn);
        const ticketsSold = ticketsSoldRaw === null || ticketsSoldRaw === undefined || ticketsSoldRaw === ''
            ? null
            : Number(ticketsSoldRaw);
        const checkedIn = checkedInRaw === null || checkedInRaw === undefined || checkedInRaw === ''
            ? null
            : Number(checkedInRaw);
        const hasAttendance = Boolean(eventName)
            && Number.isFinite(ticketsSold)
            && Number.isFinite(checkedIn);

        return (
            <div className="page-wrapper">
                <div className="page-content">
                    <div className="tab_header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <button
                            className="btn btn-sm"
                            style={{ background: '#7e57c2', color: '#fff', fontWeight: 700 }}
                            onClick={() => this.props.history.push('/admin/sales')}
                        >
                            <i className="zmdi zmdi-arrow-left" aria-hidden="true"></i> Back
                        </button>
                        <h1 style={{ margin: 0 }}>
                            <span className="d-block">Work Period Detail</span>
                        </h1>
                    </div>

                    {bannerTitle ? (
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            flexWrap: 'wrap',
                            alignItems: 'stretch',
                            marginBottom: '8px',
                        }}>
                            <div style={{
                                ...cardStyle,
                                flex: '1 1 280px',
                                minWidth: 0,
                                borderLeft: '3px solid #7e57c2',
                            }}>
                                <div style={cardLabelStyle}>{bannerLabel}</div>
                                <div style={{ ...cardValueStyle, fontSize: '1.1rem' }}>{bannerTitle}</div>
                                {eventName && (instanceDate || organizer) ? (
                                    <div style={{
                                        marginTop: '6px',
                                        color: 'var(--text-light2)',
                                        fontSize: '0.85rem',
                                        display: 'flex',
                                        gap: '8px',
                                        flexWrap: 'wrap',
                                    }}>
                                        {instanceDate ? <span>{formatApiDateLong(instanceDate, '')}</span> : null}
                                        {instanceDate && organizer ? <span>Â·</span> : null}
                                        {organizer ? <span>{organizer}</span> : null}
                                    </div>
                                ) : null}
                            </div>
                            {hasAttendance ? (
                                <div style={{
                                    ...cardStyle,
                                    flex: '0 1 220px',
                                    marginLeft: 'auto',
                                    borderLeft: '3px solid #7e57c2',
                                }}>
                                    <div style={cardLabelStyle}>Attendance</div>
                                    <div style={{
                                        marginTop: '6px',
                                        display: 'flex',
                                        gap: '20px',
                                        flexWrap: 'wrap',
                                    }}>
                                        <div>
                                            <div style={{ ...cardLabelStyle, marginBottom: '2px' }}>Tickets sold</div>
                                            <div style={cardValueStyle}>{ticketsSold.toLocaleString()}</div>
                                        </div>
                                        <div>
                                            <div style={{ ...cardLabelStyle, marginBottom: '2px' }}>Checked in</div>
                                            <div style={cardValueStyle}>{checkedIn.toLocaleString()}</div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Row 1: Period Info */}
                    <div style={sectionTitleStyle}>Period Info</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <SummaryCard label="Workperiod Start" value={this.formatDateTime(this.v(detail, 'start', 'Start', 'startUtc', 'StartUtc'))} color="#7e57c2" />
                        <div style={cardStyle}>
                            <div style={cardLabelStyle}>Workperiod End</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div style={{ ...cardValueStyle, color: this.isStillActive(detail) ? '#2ecc71' : '#7e57c2' }}>
                                    {this.isStillActive(detail)
                                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ecc71', display: 'inline-block', animation: 'pulse-dot 1.5s ease-in-out infinite' }}></span>
                                            {this.props.t('report.stillActive')}
                                          </span>
                                        : this.formatDateTime(this.v(detail, 'end', 'End', 'endUtc', 'EndUtc'))}
                                </div>
                                {this.isStillActive(detail) && (
                                    <button
                                        onClick={this.handleEndPeriod}
                                        disabled={this.props.toggling}
                                        style={{
                                            background: '#e74c3c',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '3px 10px',
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            cursor: this.props.toggling ? 'not-allowed' : 'pointer',
                                            opacity: this.props.toggling ? 0.6 : 1,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            whiteSpace: 'nowrap',
                                            lineHeight: '1.4',
                                        }}
                                    >
                                        {this.props.toggling ? 'Processing...' : 'End Period'}
                                    </button>
                                )}
                            </div>
                        </div>
                        <SummaryCard label="Transactions" value={this.v(detail, 'transactions', 'Transactions')} />
                        <SummaryCard
                            label="Total Hours"
                            value={this.isStillActive(detail) ? this.getElapsedHours(detail) : this.formatHours(this.v(detail, 'totalHours', 'TotalHours'))}
                            color={this.isStillActive(detail) ? '#2ecc71' : undefined}
                        />
                    </div>

                    {/* Row 2: Sales Breakdown */}
                    <div style={sectionTitleStyle}>Sales Breakdown</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <SummaryCard
                            label={`${this.props.t('workperiods.cash')} (${this.v(cash, 'count', 'Count')})`}
                            value={this.formatCurrency(this.v(cash, 'amount', 'Amount'))}
                        />
                        <SummaryCard
                            label={`${this.props.t('workperiods.net')} (${this.v(card, 'count', 'Count')})`}
                            value={this.formatCurrency(this.v(card, 'amount', 'Amount'))}
                        />
                        {Math.abs(memberAmt) >= 0.005 && (
                            <SummaryCard
                                label={`${this.props.t('report.member')} (${this.v(membership, 'count', 'Count')})`}
                                value={this.formatCurrency(memberAmt)}
                            />
                        )}
                        {Math.abs(adminFeesAmt) >= 0.005 && (
                            <SummaryCard
                                label={this.props.t('workperiods.adminFees')}
                                value={this.formatCurrency(adminFeesAmt)}
                            />
                        )}
                        <SummaryCard
                            label={this.props.t('report.salesBeforeRefunds')}
                            value={this.formatCurrency(netSales)}
                            color="#16a085"
                        />
                        {Math.abs(Number(this.v(detail, 'tax', 'Tax') || 0)) >= 0.005 ? (
                            <SummaryCard
                                label={this.props.t('report.taxCollected')}
                                value={this.formatCurrency(this.v(detail, 'tax', 'Tax'))}
                                note={this.props.t('report.taxNetHelp')}
                            />
                        ) : null}
                    </div>

                    {(() => {
                        const merchRefunds = Number(this.v(detail, 'merchRefunds', 'MerchRefunds') || 0);
                        const discounts = Number(this.v(detail, 'discounts', 'Discounts') || 0);
                        const comps = Number(this.v(detail, 'comps', 'Comps') || 0);
                        const customerRefunds = Number(this.v(detail, 'customerRefunds', 'CustomerRefunds')
                            || this.v(detail, 'refunds', 'Refunds') || 0);
                        const tipRefunded = Number(this.v(detail, 'tipRefundedDisplay', 'TipRefundedDisplay') || 0);
                        const gratRefunded = Number(this.v(detail, 'gratuityRefundedDisplay', 'GratuityRefundedDisplay') || 0);
                        const taxRefunded = Number(this.v(detail, 'taxRefunded', 'TaxRefunded') || 0);
                        const taxNet = Number(this.v(detail, 'taxNet', 'TaxNet') || 0);
                        const estimated = !!(
                            (detail.merchRefundEstimated ?? detail.MerchRefundEstimated)
                            || (detail.tipGratRefundEstimated ?? detail.TipGratRefundEstimated)
                        );
                        if (!(merchRefunds > 0 || discounts > 0 || comps > 0 || tipRefunded > 0 || gratRefunded > 0 || taxRefunded > 0)) {
                            return null;
                        }
                        return (
                            <>
                                <div style={sectionTitleStyle}>{this.props.t('report.afterRefunds')}</div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                    {discounts > 0 ? (
                                        <SummaryCard label={this.props.t('report.discounts')} value={this.formatCurrency(discounts)} />
                                    ) : null}
                                    {comps > 0 ? (
                                        <SummaryCard label={this.props.t('report.comps')} value={this.formatCurrency(comps)} />
                                    ) : null}
                                    {merchRefunds > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.merchRefunds')}
                                            value={this.formatCurrency(merchRefunds)}
                                            note={this.props.t('report.merchRefundsHelp')}
                                        />
                                    ) : null}
                                    {customerRefunds > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.customerRefunds')}
                                            value={this.formatCurrency(customerRefunds)}
                                            note={this.props.t('report.customerRefundsHelp')}
                                        />
                                    ) : null}
                                    {taxRefunded > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.taxRefunded')}
                                            value={this.formatCurrency(taxRefunded)}
                                        />
                                    ) : null}
                                    {taxRefunded > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.taxNet')}
                                            value={this.formatCurrency(taxNet)}
                                            note={this.props.t('report.taxNetHelp')}
                                        />
                                    ) : null}
                                    {tipRefunded > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.netTips')}
                                            value={this.formatCurrency(this.v(detail, 'netTipsDisplay', 'NetTipsDisplay'))}
                                        />
                                    ) : null}
                                    {gratRefunded > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.netGratuity')}
                                            value={this.formatCurrency(this.v(detail, 'netGratuityDisplay', 'NetGratuityDisplay'))}
                                        />
                                    ) : null}
                                    {merchRefunds > 0 ? (
                                        <SummaryCard
                                            label={this.props.t('report.salesAfterRefunds')}
                                            value={this.formatCurrency(this.v(detail, 'netSalesMerch', 'NetSalesMerch'))}
                                            color="#16a085"
                                        />
                                    ) : null}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-light2)', margin: '0 0 16px' }}>
                                    {merchRefunds > 0 ? (
                                        <p style={{ margin: '0 0 6px' }}>{this.props.t('report.merchRefundsHelp')}</p>
                                    ) : null}
                                    {customerRefunds > 0 ? (
                                        <p style={{ margin: '0 0 6px' }}>{this.props.t('report.customerRefundsHelp')}</p>
                                    ) : null}
                                    {taxRefunded > 0 ? (
                                        <p style={{ margin: '0 0 6px' }}>{this.props.t('report.taxNetHelp')}</p>
                                    ) : null}
                                    <p style={{ margin: 0 }}>
                                        {this.props.t(estimated ? 'report.refundEstimateFootnote' : 'report.refundNetFootnote')}
                                    </p>
                                </div>
                            </>
                        );
                    })()}

                    {/* Row 3: Tips & Payouts */}
                    <div style={sectionTitleStyle}>Tips &amp; Payouts</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
                        <SummaryCard label="Tips" value={this.formatCurrency(this.v(detail, 'tips', 'Tips'))} />
                        <SummaryCard label="Gratuity" value={this.formatCurrency(this.v(detail, 'gratuity', 'Gratuity'))} />
                        <SummaryCard label="Tips & Grat." value={this.formatCurrency(Number(this.v(detail, 'tips', 'Tips')) + Number(this.v(detail, 'gratuity', 'Gratuity')))} color="#e74c3c" />
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                flex: this.state.periodPooled ? '1 1 340px' : '1 1 160px',
                                minWidth: this.state.periodPooled ? '280px' : '140px',
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: this.state.periodPooled ? '6px' : 0,
                                    flex: '1 1 auto',
                                    minHeight: '84px',
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={this.handleDownloadCloseout}
                                    disabled={this.props.closeoutDownloading}
                                    style={downloadButtonStyle({
                                        compact: this.state.periodPooled,
                                        downloading: this.props.closeoutDownloading,
                                    })}
                                >
                                    {this.props.closeoutDownloading ? (
                                        <>
                                            <Loader size={this.state.periodPooled ? 14 : 16} />
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="zmdi zmdi-download" />
                                            Closeout Summary
                                        </>
                                    )}
                                </button>
                                {this.state.periodPooled ? (
                                    <button
                                        type="button"
                                        onClick={this.handleDownloadPooledTipReport}
                                        disabled={this.state.pooledDownloadLoading}
                                        style={pooledOutlineButtonStyle({
                                            downloading: this.state.pooledDownloadLoading,
                                        })}
                                    >
                                        {this.state.pooledDownloadLoading ? (
                                            <>
                                                <Loader size={14} />
                                                Downloading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="zmdi zmdi-download" />
                                                Pooled Tip Report
                                            </>
                                        )}
                                    </button>
                                ) : null}
                            </div>
                            {this.state.periodPooled ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                        flex: '0 0 auto',
                                        minHeight: '84px',
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => this.handleSendPooledTipReport('email')}
                                        disabled={!!this.state.sendPoolLoading || this.state.payPooledLoading || !(this.props.users || []).length}
                                        title="Email the pooled tip report to all employees who worked this period"
                                        aria-label="Email all — send the pooled tip report to all employees who worked this period"
                                        style={pooledOutlineButtonStyle({
                                            downloading: this.state.sendPoolLoading === 'email',
                                            disabled: !!this.state.sendPoolLoading || this.state.payPooledLoading || !(this.props.users || []).length,
                                        })}
                                    >
                                        {this.state.sendPoolLoading === 'email' ? (
                                            <Loader size={14} />
                                        ) : (
                                            <i className="zmdi zmdi-email" aria-hidden="true" />
                                        )}
                                        Email all
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => this.handleSendPooledTipReport('sms')}
                                        disabled={!!this.state.sendPoolLoading || this.state.payPooledLoading || !(this.props.users || []).length}
                                        title="Text the pooled tip report to all employees who worked this period"
                                        aria-label="Text all — send the pooled tip report to all employees who worked this period"
                                        style={pooledOutlineButtonStyle({
                                            downloading: this.state.sendPoolLoading === 'sms',
                                            disabled: !!this.state.sendPoolLoading || this.state.payPooledLoading || !(this.props.users || []).length,
                                        })}
                                    >
                                        {this.state.sendPoolLoading === 'sms' ? (
                                            <Loader size={14} />
                                        ) : (
                                            <i className="zmdi zmdi-smartphone" aria-hidden="true" />
                                        )}
                                        Text all
                                    </button>
                                    {this.state.pooledPayAvailable && !this.isStillActive(detail) ? (
                                    <button
                                        type="button"
                                        onClick={this.handlePayPooledTips}
                                        disabled={this.state.payPooledLoading || !!this.state.sendPoolLoading}
                                        title="Pay remaining unpaid pooled tip shares via Stripe"
                                        aria-label="Pay pooled tips via Stripe for this work period"
                                        style={pooledFillButtonStyle({
                                            loading: this.state.payPooledLoading,
                                            disabled: this.state.payPooledLoading || !!this.state.sendPoolLoading,
                                        })}
                                    >
                                        {this.state.payPooledLoading ? (
                                            <Loader size={14} />
                                        ) : (
                                            <i className="zmdi zmdi-money" aria-hidden="true" />
                                        )}
                                        {this.state.payPooledLoading ? 'Paying…' : 'Pay pooled tips'}
                                    </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* User Aggregates Table */}
                    <Widget>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '12px', color: 'var(--white)' }}>User Sales for this Work Period</h3>
                        {usersLoading ? <Loader size={40} /> : (
                            <div className="order_list">
                                <NativeTable
                                    data={users}
                                    searchFields={['username', 'Username', 'userName', 'UserName']}
                                    columns={[
                                        {
                                            key: 'username',
                                            header: 'User',
                                            render: (cell, row) => {
                                                const userId = row.userId || row.UserId || row.id || row.Id;
                                                const periodId = this.props.match.params.periodId;
                                                const name = row.username || row.Username || row.userName || row.UserName || '—';
                                                const cashObj = row.cash || row.Cash || {};
                                                const cardObj = row.card || row.Card || {};
                                                const memberObj = row.membership || row.Membership || {};
                                                const cardFee = Number(this.v(cardObj, 'fee', 'Fee') || 0);
                                                const memberFee = Number(this.v(memberObj, 'fee', 'Fee') || 0);
                                                const rowFees = Number(this.v(row, 'fees', 'Fees') || 0);
                                                return (
                                                    <Link
                                                        to={{
                                                            pathname: `/admin/users/sales/${userId}/workperiod/${periodId}`,
                                                            state: {
                                                                workperiod: {
                                                                    WorkperiodId: periodId,
                                                                    WorkPeriodId: periodId,
                                                                    id: row.id || row.Id,
                                                                    Tips: this.v(row, 'tips', 'Tips'),
                                                                    Gratuity: this.v(row, 'gratuity', 'Gratuity'),
                                                                    CashSales: this.v(cashObj, 'amount', 'Amount'),
                                                                    CardSales: this.v(cardObj, 'amount', 'Amount'),
                                                                    MemberSales: this.v(memberObj, 'amount', 'Amount'),
                                                                    Fees: rowFees || (cardFee + memberFee),
                                                                    CardSalesExcludeFees: true,
                                                                    Hours: this.v(row, 'hoursWorked', 'HoursWorked'),
                                                                    WorkperiodDate: (() => {
                                                                        const startRaw = this.v(detail, 'start', 'Start', 'startUtc', 'StartUtc');
                                                                        return apiDateToYmd(startRaw) || startRaw;
                                                                    })(),
                                                                    ClockIn: this.v(row, 'clockIn', 'ClockIn'),
                                                                    ClockOut: this.v(row, 'clockOut', 'ClockOut'),
                                                                    TipPayoutStatus: row.tipPayoutStatus || row.TipPayoutStatus || null,
                                                                    TransferId: row.transferId || row.TransferId || null,
                                                                },
                                                                employee: { Username: name }
                                                            }
                                                        }}
                                                        style={{ color: '#7e57c2', fontWeight: 600, textDecoration: 'none' }}
                                                    >
                                                        {name}
                                                    </Link>
                                                );
                                            },
                                        },
                                        {
                                            key: 'hoursWorked',
                                            header: 'Hours',
                                            render: (cell, row) => this.formatHours(this.v(row, 'hoursWorked', 'HoursWorked')),
                                        },
                                        {
                                            key: 'transactions',
                                            header: 'Trns.',
                                            render: (cell, row) => this.v(row, 'transactions', 'Transactions'),
                                        },
                                        {
                                            key: 'totalSales',
                                            header: this.props.t('workperiods.charges'),
                                            // Merch only (Net / TotalSales after fee separation).
                                            render: (cell, row) => this.formatCurrency(
                                                this.v(row, 'net', 'Net', 'totalSales', 'TotalSales')
                                            ),
                                        },
                                        {
                                            key: 'card',
                                            header: this.props.t('workperiods.card'),
                                            render: (cell, row) => {
                                                const c = row.card || row.Card || {};
                                                return this.formatCurrency(this.v(c, 'amount', 'Amount'));
                                            },
                                        },
                                        {
                                            key: 'cash',
                                            header: this.props.t('workperiods.cash'),
                                            render: (cell, row) => {
                                                const c = row.cash || row.Cash || {};
                                                return this.formatCurrency(this.v(c, 'amount', 'Amount'));
                                            },
                                        },
                                        {
                                            key: 'tips',
                                            header: 'Tips',
                                            render: (cell, row) => this.formatCurrency(this.v(row, 'tips', 'Tips')),
                                        },
                                        {
                                            key: 'gratuity',
                                            header: 'Gratuity',
                                            render: (cell, row) => this.formatCurrency(this.v(row, 'gratuity', 'Gratuity')),
                                        },
                                        {
                                            key: 'payout',
                                            header: 'Payout',
                                            render: (cell, row) => this.formatCurrency(this.v(row, 'payout', 'Payout')),
                                        },
                                        {
                                            key: 'clockIn',
                                            header: 'Clock In',
                                            render: (cell, row) => this.formatTime(this.v(row, 'clockIn', 'ClockIn')),
                                        },
                                        {
                                            key: 'clockOut',
                                            header: 'Clock Out',
                                            render: (cell, row) => {
                                                const val = this.v(row, 'clockOut', 'ClockOut');
                                                if (!val || String(val).trim() === '' || String(val).trim() === '0001-01-01T00:00:00') {
                                                    return <span style={{ color: '#2ecc71', fontWeight: 600 }}>{this.props.t('report.stillActive')}</span>;
                                                }
                                                return this.formatTime(val);
                                            },
                                        },
                                    ]}
                                />
                            </div>
                        )}
                    </Widget>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        detail: state.sales?.workperiodDetail || null,
        detailLoading: state.sales?.workperiodDetailLoading || false,
        users: state.sales?.workperiodUsers || [],
        usersLoading: state.sales?.workperiodUsersLoading || false,
        toggling: state.sales?.workperiodToggling || false,
        closeoutDownloading: state.sales?.workperiodCloseoutDownloading || false,
    };
}

export default withRouter(connect(mapStateToProps)(withTranslation(WorkperiodDetail)));
